import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, IconButton, Modal, Typography, MenuItem } from '@mui/material';
import { useSnackbar } from 'notistack';
import { Elements, ElementsConsumer, PaymentElement } from '@stripe/react-stripe-js';
import CloseIcon from '@mui/icons-material/CloseRounded';
import Image from 'next/legacy/image';
import { isValid } from 'date-fns';
import { useMutation } from '@tanstack/react-query';
import {
	EventBackground,
	getReponseDataFromAxiosError,
	Showable,
	TicketIcon,
	WarningModal,
	OrderSummary,
} from '@sweatpals/react-ui-kit';
import {
	FileVariantsEnum,
	GetPricesAndFeesDto,
	formatEventTimeRange,
	EventTicketDto,
	OrderDto,
	waitAsync,
	priceFormatter,
	EventPriceTypesEnum,
} from '@sweatpals/shared';
import { EventType } from '../../../services/types/event-types';
import { paymentService, stripeService } from '../../../services';
import { getPublicFileUrl } from '../../../utils/file.utils';
import { upgradeTicketFormModalStyles } from './upgrade-ticket-form-modal.styles';
import { useStyles } from '../../../utils/hooks/useStyles';
import Logo from '../../ui-v2/logo/logo.component';
import loader from '../../../public/assets/images/loader.gif';
import { CoreSelect } from '../core-select';
import { CheckIcon } from '../../icons';
import { getMoreExpensivePrices } from '../../../utils/getMoreExpensivePrices';
import useDebounce from '../../../utils/hooks/useDebounce';
import { StripeElements, PaymentIntentConfirmParams } from '@stripe/stripe-js';
import { CoreButtonBase } from '../core-button';
import { HOME_PAGE_URL } from '../../../constants';

export interface UpgradeTicketFormModalProps {
	open: boolean;
	ticket: EventTicketDto;
	eventData: EventType;
	freeSpotsCount?: number;
	onClose(): void;
	refetchOrder?(): Promise<unknown>;
}

const UpgradeTicketFormModal: FC<UpgradeTicketFormModalProps> = (props) => {
	const { open, eventData, ticket, refetchOrder, onClose } = props;
	const {
		name: eventName,
		startDate,
		endDate,
		author,
		tzid,
		isDonationBased,
		background,
		prices,
		additionalData,
	} = eventData;

	const moreExpensivePrices = useMemo(
		() => (
			eventData.prices && ticket.eventPrice
				? getMoreExpensivePrices(
						eventData.prices.filter(price => price.tierTypeId !== EventPriceTypesEnum.Unlisted), ticket.eventPrice
					)
				: []
		),
		[eventData, ticket],
	);

	const [priceAndFees, setPriceAndFees] = useState<GetPricesAndFeesDto | null>(null);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [waringModalIsOpen, setWaringModalIsOpen] = useState(false);
	const [selectedPriceId, setSelectedPriceId] = useState(moreExpensivePrices[0]?.id);

	const { debounceFunc } = useDebounce(1000);
	const { enqueueSnackbar } = useSnackbar();
	const formWrapperRef = useRef<HTMLDivElement | null>(null);
	const styles = useStyles(upgradeTicketFormModalStyles);

	const selectedPrice = useMemo(
		() => (eventData.prices ?? []).find(p => p.id === selectedPriceId),
		[eventData, selectedPriceId]
	);

	const { mutateAsync: createPaymentIntent } = useMutation(
		['paymentIntentToUpgradeTicketV2', ticket.id, selectedPriceId],
		async () => {
			if (!selectedPriceId) {
				return;
			}

			return paymentService.createPublicPaymentIntentToUpgradeTicketV2({
				ticketId: ticket.id,
				eventPriceId: selectedPriceId,
			});
		},
		{
			onError: (err: Error) => {
				const data = getReponseDataFromAxiosError(err);

				enqueueSnackbar(data?.message || err.message, { variant: 'error' });
			},
		}
	);

	const {
		data: newPriceAndFees,
		isLoading: isPriceAndFeesLoading,
		mutateAsync: getPriceAndFees,
	} = useMutation(
		['pricesAndFeesToUpgradeTicketV2', ticket.id, selectedPriceId],
		async () => {
			if (!selectedPriceId) {
				return;
			}

			return paymentService.getPublicPricesAndFeesToUpgradeTicketV2({
				ticketId: ticket.id,
				eventPriceId: selectedPriceId,
			});
		},
		{
			onError: (err: Error) => {
				const data = getReponseDataFromAxiosError(err);

				enqueueSnackbar(data?.message || err.message, { variant: 'error' });
			},
		}
	);

	useEffect(() => {
		if (newPriceAndFees && !isPriceAndFeesLoading) {
			setPriceAndFees(newPriceAndFees);
		}
	}, [newPriceAndFees, isPriceAndFeesLoading]);

	useEffect(() => {
		getPriceAndFees();
	}, []);

	const getOrder = useCallback(async (orderId: string): Promise<OrderDto | undefined> => {
		try {
			let order: OrderDto | undefined;
			let attempts = 0;

			while (!order && attempts <= 10) {
				attempts++;
				await waitAsync(5000);
				order = await paymentService.getOrderByIdOrPaymentIntentId(orderId);
			}

			return order;
		} catch (e) {
			return;
		}
	}, []);

	const onSubmit = useCallback(async (elements: StripeElements): Promise<void> => {
		setIsSubmitting(true);

		try {
			const paymentIntent = await createPaymentIntent();

			if (!paymentIntent.intent) {
				return;
			}

			const confirmParams: PaymentIntentConfirmParams = {
				return_url: paymentIntent.orderId ? `${HOME_PAGE_URL}/order/${paymentIntent.orderId}` : undefined,
			};
			const response = await stripeService.confirmCardPayment(elements, paymentIntent.intent.paymentIntent, confirmParams);

			if (response.error) {
				enqueueSnackbar(response.error.message, { variant: 'error' });

				return;
			}

			if (response.paymentIntent.status !== 'succeeded') {
				return;
			}

			const order = await getOrder(paymentIntent.orderId);

			if (!order) {
				setWaringModalIsOpen(true);

				return;
			}

			await refetchOrder?.();
			onClose();
			enqueueSnackbar('Ticket was successfully upgraded', { variant: 'success' });
		} finally {
			setIsSubmitting(false);
		}
	}, [createPaymentIntent, enqueueSnackbar, getOrder, onClose, refetchOrder]);

	return (
		<Modal open={open}>
			<Box sx={styles.root}>
				<EventBackground background={background && getPublicFileUrl(background, { variant: FileVariantsEnum.S })} />
				<Box sx={styles.content}>
					<Box display="flex" justifyContent="space-between" alignItems="center">
						<Logo type="light" sx={{ cursor: 'default', height: '21px' }} layout="fill" />
						<IconButton sx={styles.closeIcon} disabled={isSubmitting} onClick={onClose}>
							<CloseIcon />
						</IconButton>
					</Box>
					<Showable show={!moreExpensivePrices.length}>
						<Box display="flex" alignItems="center" justifyContent="center" height="100%">
							<Box sx={styles.placeholder}>
								<Typography sx={styles.placeholderIcon}>🌊</Typography>
								<Box display="flex" flexDirection="column" gap="8px">
									<Typography sx={styles.placeholderTitle}>
										{`You're Already in the Best Spot!`}
									</Typography>
									<Typography sx={styles.placeholderDescription}>
										{`You're all set! No higher upgrade options are available at the moment.`}
									</Typography>
								</Box>
								<Box display="flex" justifyContent="center">
									<CoreButtonBase
										variant="secondary"
										sx={styles.viewTicketsButton}
										onClick={onClose}
									>
										View my Tickets
									</CoreButtonBase>
								</Box>
							</Box>
						</Box>
					</Showable>
					<Showable show={moreExpensivePrices.length}>
						<Box sx={styles.body}>
							<Box sx={styles.formBody}>
								<Box sx={{ width: { xs: '100%', md: '320px' }, display: 'flex', flexDirection: 'column', gap: '12px' }}>
									<Box>
										<Box sx={{ ...styles.eventInfo, borderRadius: eventData.isPaid ? '8px 8px 0 0' : '8px' }}>
											{background ? (
												<Box
													sx={{
														...styles.eventBackground,
														backgroundImage: `url(${getPublicFileUrl(background, {
															variant: FileVariantsEnum.M,
														})})`,
													}}
												/>
											) : null}
											<Box sx={{ gap: '4px', display: 'flex', flexDirection: 'column' }}>
												<Box sx={styles.eventName}>{eventName}</Box>
												<Showable show={isValid(new Date(startDate)) && !additionalData?.hideEventDate}>
													<Typography sx={styles.eventDate}>
														{startDate ? formatEventTimeRange(startDate, endDate, {
															timezone: tzid,
															startDateFormat: 'eee MMM do @ h:mm aa',
															endDateFormat: 'eee MMM do @ h:mm aa',
														}) : ''}
													</Typography>
												</Showable>
											</Box>
										</Box>
										<Showable show={eventData.isPaid}>
											<Box position="relative" zIndex={1}>
												<svg
													style={{
														height: '1px',
														width: '100%',
														position: 'absolute',
														bottom: 0,
														left: 0,
														strokeWidth: '1px',
													}}
												>
													<line x1="0" y1="0" x2="100%" y2="0" stroke="#FFFFFF1A" strokeDasharray="6 6" />
												</svg>
											</Box>
										</Showable>
										<Showable show={eventData.isPaid}>
											<Box sx={styles.orderSummary}>
												<Showable show={isPriceAndFeesLoading}>
													<Box sx={styles.loader}>
														<Image src={loader} width={64} height={64} alt={'google-play'} />
													</Box>
												</Showable>
												<Box
													sx={{
														height: { xs: 'auto', md: '100%' },
														overflow: { xs: 'unset', md: 'auto' },
														p: '0 12px 1px',
														opacity: isPriceAndFeesLoading ? 0.2 : 1,
													}}
												>
													<OrderSummary
														upgradedTicket={selectedPrice && {
															eventPriceId: selectedPrice.id,
															tierName: selectedPrice.tierName,
															priceAmount: priceAndFees?.ticketsBasePrice ?? 0,
														}}
														prices={prices}
														priceAndFees={priceAndFees}
														isDonationBased={isDonationBased}
													/>
												</Box>
											</Box>
										</Showable>
									</Box>
								</Box>
								<Box
									ref={formWrapperRef}
									sx={{
										flex: { xs: 'unset', md: 1 },
										overflowX: 'hidden',
										overflowY: { xs: 'unset', md: 'auto' },
									}}
								>
									<Box sx={styles.form}>
										<Box display="flex" flexDirection="column" gap="16px">
											<Box display="flex" flexDirection="column" gap="8px">
												<Typography sx={styles.title}>Upgrade your ticket</Typography>
												<Typography sx={styles.description}>
													You will be required to pay the difference of the ticket
												</Typography>
											</Box>
											<Box sx={styles.price}>
												<Typography sx={styles.priceTitle}>
													Your Current Ticket Tier
												</Typography>
												<Box sx={styles.priceInfo}>
													<TicketIcon />
													<Box display="flex" flexDirection="column" gap="8px" flex={1}>
														<Typography sx={styles.priceName}>
															{ticket.eventPrice?.tierName}
														</Typography>
														<Showable show={ticket.eventPrice?.tierDescription}>
															<Typography sx={styles.priceDescription}>
																{ticket.eventPrice?.tierDescription}
															</Typography>
														</Showable>
													</Box>
													<Typography sx={styles.priceAmount}>
														{priceFormatter(ticket.eventPrice?.priceAmount ?? 0)}
													</Typography>
												</Box>
											</Box>
											<CoreSelect
												variant="outlined"
												label="Change to"
												required
												requiredMark
												value={selectedPriceId}
												disabled={isSubmitting}
												renderValue={() => (
													<Box display='flex' gap='10px'>
														<TicketIcon />
														<Showable show={selectedPrice}>
															{selectedPrice.tierName} — {priceFormatter(selectedPrice.priceAmount ?? 0)}
														</Showable>
													</Box>
												)}
												onChange={(e) => {
													setSelectedPriceId(e.target.value);
													debounceFunc(async () => {
														await getPriceAndFees();
													});
												}}
												paperSx={styles.pricesSelectPaper}
											>
												{moreExpensivePrices.map(price => (
													<MenuItem
														key={price.id}
														value={price.id}
														sx={styles.pricesSelectItem}
													>
														<Box display="flex" justifyContent="space-between" gap="10px">
															<Typography sx={styles.priceSelectItemTitle}>
																{price.tierName} — {priceFormatter(price.priceAmount ?? 0)}
															</Typography>
															<Showable show={price.id === selectedPriceId}>
																<CheckIcon sx={{ width: '24px', height: '24px' }} />
															</Showable>
														</Box>
														<Showable show={price.tierDescription}>
															<Typography sx={styles.priceSelectItemDescription}>
																{price.tierDescription}
															</Typography>
														</Showable>
													</MenuItem>
												))}
											</CoreSelect>
										</Box>
										<Box display="flex" flexDirection="column" gap="24px">
											<Typography sx={styles.title}>Payment</Typography>
											<Box position="relative">
												<Elements
													stripe={stripeService.stripe}
													options={{
														amount: priceAndFees?.fullPrice ?? 50,
														mode: 'payment',
														currency: 'usd',
														payment_method_types: ['card'],
														setup_future_usage: 'off_session',
														loader: 'always',

														onBehalfOf: author?.stripeConnectedAccountId,

														appearance: {
															theme: 'night',
															variables: {
																colorText: '#FFFFFF',
																colorTextPlaceholder: '#FFFFFF99',
																colorTextSecondary: '#C6C6C6',
																colorIconRedirect: '#656565',
																colorIconTab: '#FFFFFF',
																colorIconTabHover: '#FFFFFF',
																colorIconTabSelected: '#FFFFFF',
															},
															rules: {
																'.Label': {
																	fontSize: '15px',
																	marginBottom: '8px',
																},
																'.Input': {
																	// SWTP-1350
																	// This is a bug in all browsers when using native select input with transparent background - leeds to white background of dropdown options
																	// https://codepen.io/AmeliaBR/pres/PpEPGz
																	// But in stripe we dont have an css selector only for dropdown options (e.g. `.Input option`), so using `.Input` selector
																	// To fix this we need to set it to solid color, in order to propagate background to dropdown options
																	backgroundColor: '#1D1D1D', // same as '#FFFFFF0D',
																	borderWidth: '0px',
																	boxShadow: 'none',
																},
																'.Input:focus': {
																	boxShadow: '0 0 0 2px #2B2D2F',
																},
																'.Tab': {
																	backgroundColor: '#FFFFFF0D',
																	color: '#FFFFFF',
																	fontSize: '13px',
																	fontWeight: '500',
																	lineHeight: '16px',
																	border: '2px solid #FFFFFF0D',
																	boxShadow: '0px 2px 4px 0px #00000012',
																},
																'.Tab:hover': {
																	borderColor: '#FFFFFF',
																	boxShadow: '0px 2px 4px 0px #00000012',
																},
																'.Tab:focus': {
																	borderColor: '#FFFFFF',
																	boxShadow: '0px 2px 4px 0px #00000012',
																},
																'.Tab--selected': {
																	color: '#FFFFFF',
																	borderColor: '#FFFFFF',
																	boxShadow: '0px 2px 4px 0px #00000012',
																},
																'.Tab--selected:hover': {
																	color: '#FFFFFF',
																	borderColor: '#FFFFFF',
																	boxShadow: '0px 2px 4px 0px #00000012',
																},
																'.Tab--selected:focus': {
																	color: '#FFFFFF',
																	borderColor: '#FFFFFF',
																	boxShadow: '0px 2px 4px 0px #00000012',
																},
																'.Block': {
																	backgroundColor: '#00000033',
																	borderRadius: '4px',
																	padding: '12px',
																	fontSize: '15px',
																	fontWeight: '600',
																	lineHeight: '21px',
																	borderWidth: '0px',
																},
																'.c-BlockDivider--horizontal': {
																	marginTop: '12px',
																	marginBottom: '10px',
																	backgroundColor: '#FFFFFF0D',
																},
																'.RedirectText': {
																	fontSize: '13px',
																	lineHeight: '16px',
																},
															},
														},
													}}
												>
													<ElementsConsumer>
														{({ elements }) => (
															<Box
																sx={{
																	display: 'flex',
																	flexDirection: 'column',
																	gap: '24px',
																	opacity: isSubmitting ? 0.2 : 1,
																}}
															>
																<PaymentElement
																	id="payment-element"
																	options={{
																		layout: 'tabs',
																	}}
																/>
																<Box sx={{ display: 'flex', justifyContent: 'center' }}>
																	<Button
																		sx={styles.submitButton}
																		disabled={isSubmitting}
																		onClick={() => {
																			onSubmit(elements);
																		}}
																	>
																		Pay with card
																	</Button>
																</Box>
															</Box>
														)}
													</ElementsConsumer>
												</Elements>
												<Showable show={isSubmitting}>
													<Box sx={styles.loader}>
														<Image src={loader} width={64} height={64} alt="google-play" />
													</Box>
												</Showable>
											</Box>
										</Box>
									</Box>
								</Box>
							</Box>
						</Box>
					</Showable>
				</Box>
				<WarningModal
					open={waringModalIsOpen}
					title="Processing Your Payment…"
					text="Your payment is on its way! We're finalizing the details, and you’ll receive your ticket(s) via email shortly."
					onClose={() => {
						onClose();
						setWaringModalIsOpen(false);
					}}
				/>
			</Box>
		</Modal>
	);
};

export default UpgradeTicketFormModal;
